import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import DefaultRedirect from './components/DefaultRedirect';
import Layout from './components/Layout';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AppDownloadRedirect from './pages/AppDownloadRedirect';
import Dashboard from './pages/Dashboard';
import AttendanceView from './pages/AttendanceView';
import UserManagement from './pages/UserManagement';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import LoginLogs from './pages/LoginLogs';
import LeaveManagement from './pages/LeaveManagement';
import UserDashboard from './pages/UserDashboard';
import UserDashboardRedirect from './components/UserDashboardRedirect';
import ManagerDashboard from './pages/ManagerDashboard';
import ClickUpAnalytics from './pages/ClickUpAnalytics';
import OrbitAnalytics from './pages/OrbitAnalytics';
import FeatureAnnouncementManager from './pages/FeatureAnnouncementManager';
import TeamHierarchy from './pages/TeamHierarchy';
import LocationManagement from './pages/LocationManagement';
import SecurityLogs from './pages/SecurityLogs';
import DeviceSecurity from './pages/DeviceSecurity';
import Announcements from './pages/Announcements';
import AdminAnnouncements from './pages/AdminAnnouncements';
import KpiHub from './pages/KpiHub';
import MyKpi from './pages/MyKpi';
import ManagerKpi from './pages/ManagerKpi';
import FeedbackInbox from './pages/FeedbackInbox';
import MyFeedback from './pages/MyFeedback';
import FeedbackChatbot from './components/FeedbackChatbot';
import EmailLogs from './pages/EmailLogs';
import EmailStats from './pages/EmailStats';
import FeatureAnnouncementGlobal from './components/FeatureAnnouncementGlobal';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { initializeMobileApp } from './utils/capacitor';
import CustomSplashScreen from './components/SplashScreen';
import NetworkBanner from './components/NetworkBanner';
import AppUpdatePrompt from './components/AppUpdatePrompt';

function AppContent() {
  const { user } = useAuth();
  
  return (
    <>
      <FeatureAnnouncementGlobal />
      <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/download" element={<AppDownloadRedirect />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            
            {/* User Dashboard Route */}
            <Route 
              path="/kpi" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <KpiHub />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/kpi/manager" 
              element={
                <ProtectedRoute>
                  <ManagerKpi />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/user-dashboard" 
              element={
                <ProtectedRoute requireUser>
                  <UserDashboardRedirect />
                  <UserDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/my-kpi" 
              element={
                <ProtectedRoute requireUser>
                  <MyKpi />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/my-feedback" 
              element={
                <ProtectedRoute requireUser>
                  <MyFeedback />
                </ProtectedRoute>
              } 
            />
            
            {/* Manager Dashboard Route */}
            <Route 
              path="/manager-dashboard" 
              element={
                <ProtectedRoute>
                  <ManagerDashboard />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/device-security"
              element={
                <ProtectedRoute>
                  <DeviceSecurity />
                </ProtectedRoute>
              }
            />
            
            {/* Admin Routes */}
            <Route 
              path="/" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/attendance" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <AttendanceView />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/users" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <UserManagement />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/reports" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <Reports />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/email-logs" 
              element={
                <ProtectedRoute requireSuperAdmin>
                  <Layout>
                    <EmailLogs />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/email-stats" 
              element={
                <ProtectedRoute requireSuperAdmin>
                  <Layout>
                    <EmailStats />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute requireSuperAdmin>
                  <Layout>
                    <Settings />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/login-logs" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <LoginLogs />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/leave-management" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <LeaveManagement />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/clickup-analytics" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <ClickUpAnalytics />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/orbit-analytics" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <OrbitAnalytics />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/feature-announcements" 
              element={
                <ProtectedRoute requireSuperAdmin>
                  <Layout>
                    <FeatureAnnouncementManager />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/team-hierarchy" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <TeamHierarchy />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/locations" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <LocationManagement />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/security-logs" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <SecurityLogs />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin-announcements" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <AdminAnnouncements />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            
            {/* Announcements Route - Available to all authenticated users */}
            <Route 
              path="/announcements" 
              element={
                <ProtectedRoute>
                  <Announcements />
                </ProtectedRoute>
              } 
            />
            
            {/* Feedback Inbox Route - Available to managers/admins */}
            <Route 
              path="/feedback-inbox" 
              element={
                <ProtectedRoute>
                  <FeedbackInbox />
                </ProtectedRoute>
              } 
            />
            
            {/* Default redirect based on authentication */}
            <Route path="*" element={<DefaultRedirect />} />
          </Routes>
          
          {/* Feedback Chatbot - Show on all authenticated pages */}
          {user && <FeedbackChatbot />}
        </>
  );
}

function App() {
  // Initialize mobile app features when running on native platform
  useEffect(() => {
    initializeMobileApp();
  }, []);

  return (
    <CustomSplashScreen>
      <NetworkBanner />
      <AppUpdatePrompt />
      <AuthProvider>
        <Router>
          <div className="App">
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
          <AppContent />
        </div>
      </Router>
    </AuthProvider>
    </CustomSplashScreen>
  );
}

export default App;
